import { DEFAULT_BRAND_ID } from "src/brands";

/**
 * localStorage key the OAuth departure points stash the active brand under.
 * Written by getGoogleAuthorizationUrl (src/redux/sagas/partner.sagas.js) and
 * by ZoomSetupTab before it hands the tab to the provider.
 */
export const BRAND_KEY = "brand";

/**
 * Put the pending brand back on the URL before the app boots.
 *
 * The brand is resolved live from `?brand=<id>` (Utility.getBrandId), but the
 * two consumers that decide what the session looks like are boot-time only:
 * applyBrandTheme() stamps `data-brand` on <html> once, and BrandProvider
 * captures the brand once, outside the router. An OAuth provider (Google
 * Calendar, Zoom) returns to a fixed redirect URI carrying only its own
 * params, so by the time either runs the brand is gone from the URL. The
 * session silently downgrades to Cialfo, and because the counselor's routes
 * are gated on `visitsRedesign`, the very screen holding the `code` is never
 * registered -- the wildcard redirects to /webinars and the code is dropped.
 *
 * Appending the brand to a client-side navigation afterwards cannot fix that:
 * the theme attribute and the provider value have already been captured. So
 * the restore has to happen here, before React mounts.
 *
 * history.replaceState is used rather than a reload: it keeps the provider's
 * `code` and the rest of the query intact, costs no extra round trip, and
 * leaves no unbranded entry in session history.
 *
 * A brand already on the URL wins -- the stash is only a fallback.
 */
export function restorePendingBrand() {
  if (typeof window === "undefined") {
    return;
  }

  const pending = readAndClear();

  // Nothing stashed, or the stash names the default brand, which is what an
  // absent param already resolves to.
  if (!pending || pending === DEFAULT_BRAND_ID) {
    return;
  }

  const params = new URLSearchParams(window.location.search);

  // The URL is authoritative when it already carries a brand.
  if (params.get(BRAND_KEY)) {
    return;
  }

  params.set(BRAND_KEY, pending);
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}?${params.toString()}${
      window.location.hash
    }`,
  );
}

/**
 * Read the stashed brand and clear it in one go. Consumed whether or not it
 * ends up being applied, so a stale stash cannot re-brand a later session.
 *
 * Storage access throws outright in some privacy modes, so both halves are
 * guarded; a session that cannot read the stash simply keeps the default brand.
 */
function readAndClear() {
  let pending = null;

  try {
    pending = window.localStorage.getItem(BRAND_KEY);
  } catch {
    return null;
  }

  try {
    window.localStorage.removeItem(BRAND_KEY);
  } catch {
    // Nothing to do -- the value was already read.
  }

  return pending;
}
